import { z } from "zod/v4";
import { vizViewTypeSchema } from "@/lib/ai/visualization/data/schema";
import { buildVizSeriesDisplayLabels } from "@/lib/ai/visualization/narration/display-labels";
import {
  buildEvidenceMaxBySeries,
  buildEvidenceTopRows,
  buildLatestEvidenceRow,
} from "@/lib/ai/visualization/narration/evidence";
import type {
  VizFollowupContext,
  VizFollowupDisplayLabels,
  VizFollowupRenderFormat,
  VizRenderEvidence,
  VizRenderEvidenceRow,
} from "@/lib/ai/visualization/types";
import type { VizSpec } from "@/lib/ai/visualization/views/compiled-spec";

/**
 * follow-up context は persisted internal contract で、`catalog.prompt()` には露出しない。
 *
 * @remarks
 * ここでは prompt-safe 制約より compact persistence を優先するため、`z.record()` を
 * 明示的に許可する。
 */
const vizFollowupDisplayLabelsSchema = z
  .object({
    xLabel: z.string().nullable(),
    yLabel: z.string().nullable(),
    categoryLabel: z.string().nullable(),
    valueLabel: z.string().nullable(),
    seriesLabels: z.record(z.string(), z.string()),
    columnLabels: z.record(z.string(), z.string()),
  })
  .strict();

const vizFollowupEvidenceRowSchema = z
  .object({
    label: z.string(),
    rank: z.number().optional(),
    values: z.record(z.string(), z.number().nullable()),
  })
  .strict();

const vizFollowupSeriesPeakSchema = z
  .object({
    seriesKey: z.string(),
    label: z.string(),
    value: z.number(),
  })
  .strict();

const vizFollowupRenderFormatSchema = z
  .object({
    kind: z.enum(["chart", "table"]),
    viewType: vizViewTypeSchema,
  })
  .strict();

const vizFollowupContextSchema = z
  .object({
    sourceDatasetId: z.string(),
    renderFormat: vizFollowupRenderFormatSchema,
    rowCount: z.number(),
    summary: z.string().nullable(),
    intent: z.string().optional().nullable(),
    beforeCardText: z.string().optional().nullable(),
    afterCardText: z.string().optional().nullable(),
    cardTitle: z.string().optional().nullable(),
    categoryKey: z.string().optional(),
    xKey: z.string().optional(),
    seriesKeys: z.array(z.string()),
    units: z.record(z.string(), z.string().nullable()),
    displayLabels: vizFollowupDisplayLabelsSchema.optional().nullable(),
    topRows: z.array(vizFollowupEvidenceRowSchema),
    maxBySeries: z.array(vizFollowupSeriesPeakSchema),
    firstRow: vizFollowupEvidenceRowSchema.optional().nullable(),
    latestRow: vizFollowupEvidenceRowSchema.nullable(),
  })
  .strict();

function toNonBlankString(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
}

function toLabelMap(value: unknown): Record<string, string> {
  if (!value || typeof value !== "object") return {};
  const labels = value as Record<string, unknown>;
  const entries = Object.entries(labels).flatMap(([key, rawValue]) => {
    const label = toNonBlankString(rawValue);
    if (!label) return [];
    return [[key, label] as const];
  });
  return Object.fromEntries(entries);
}

const MAX_COMPACT_SCALAR_CHARS = 160;
const MAX_FOLLOWUP_SERIES_FACTS = 4;

function toCompactScalar(value: unknown): string | null {
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.length === 0) return null;
    const characters = Array.from(trimmed);
    return characters.length <= MAX_COMPACT_SCALAR_CHARS
      ? trimmed
      : `${characters.slice(0, MAX_COMPACT_SCALAR_CHARS - 1).join("")}…`;
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  if (typeof value === "boolean") {
    return value ? "true" : "false";
  }
  return null;
}

function formatFollowupRowValues(
  values: VizRenderEvidenceRow["values"],
  seriesKeys?: string[],
  seriesLabels?: Record<string, string>,
): string | null {
  const formattedEntries = (seriesKeys ?? Object.keys(values))
    .flatMap((key) => {
      const rawValue = values[key];
      const value = toCompactScalar(rawValue);
      if (!value) return [];
      const displayLabel = toCompactScalar(seriesLabels?.[key]);
      return [`${displayLabel ?? key}=${value}`];
    })
    .slice(0, MAX_FOLLOWUP_SERIES_FACTS);

  return formattedEntries.length > 0 ? formattedEntries.join(", ") : null;
}

function formatFollowupRow(
  row: VizRenderEvidenceRow | null | undefined,
  seriesKeys?: string[],
  seriesLabels?: Record<string, string>,
): string | null {
  if (!row) return null;

  const label = toCompactScalar(row.label);
  const values = formatFollowupRowValues(row.values, seriesKeys, seriesLabels);
  if (label && values) return `${label} (${values})`;
  return label ?? values;
}

function formatSeriesLabel(
  seriesKey: string,
  seriesLabels: Record<string, string>,
): string {
  const compactSeriesKey = toCompactScalar(seriesKey) ?? "series";
  const displayLabel = toCompactScalar(seriesLabels[seriesKey]);
  const label = displayLabel ?? compactSeriesKey;
  return toCompactScalar(label) ?? compactSeriesKey;
}

/**
 * 全系列から、main model に渡す代表系列を値の大きい peak 順で選ぶ。
 *
 * @remarks
 * source order の先頭 N 件は分析上の重要度を表さない。peak を基準にすることで、
 * bounded output のまま主要系列を残し、系列数と omitted count は別 fact で完全に伝える。
 */
function selectSalientSeriesKeys(context: VizFollowupContext): string[] {
  const orderedPeaks = [...context.maxBySeries].sort(
    (left, right) =>
      right.value - left.value || left.seriesKey.localeCompare(right.seriesKey),
  );
  const selected: string[] = [];
  const selectedUnits = new Set<string>();

  // 単位が混在する view では raw magnitude を直接比較せず、まず各単位の代表を残す。
  for (const peak of orderedPeaks) {
    if (selected.length >= MAX_FOLLOWUP_SERIES_FACTS) break;
    const unit = context.units[peak.seriesKey] ?? "unknown";
    if (selectedUnits.has(unit)) continue;
    selected.push(peak.seriesKey);
    selectedUnits.add(unit);
  }

  for (const peak of orderedPeaks) {
    if (selected.length >= MAX_FOLLOWUP_SERIES_FACTS) break;
    if (!selected.includes(peak.seriesKey)) selected.push(peak.seriesKey);
  }

  for (const seriesKey of [...context.seriesKeys].sort()) {
    if (selected.length >= MAX_FOLLOWUP_SERIES_FACTS) break;
    if (!selected.includes(seriesKey)) selected.push(seriesKey);
  }

  return selected;
}

/**
 * render spec の componentProps から、会話継続向けの表示ラベル要約を抽出する。
 *
 * @remarks
 * 軸/系列/列ラベルは view ごとに presence が異なるため、共通構造へ正規化する。
 */
function buildDisplayLabelSummary(
  spec: VizSpec,
): VizFollowupDisplayLabels | null {
  const props =
    spec.componentProps && typeof spec.componentProps === "object"
      ? (spec.componentProps as Record<string, unknown>)
      : {};

  const labels: VizFollowupDisplayLabels = {
    xLabel: toNonBlankString(props.xLabel),
    yLabel: toNonBlankString(props.yLabel),
    categoryLabel: toNonBlankString(props.categoryLabel),
    valueLabel: toNonBlankString(props.valueLabel),
    seriesLabels: buildVizSeriesDisplayLabels(spec),
    columnLabels: toLabelMap(props.columnLabels),
  };

  if (
    !labels.xLabel &&
    !labels.yLabel &&
    !labels.categoryLabel &&
    !labels.valueLabel &&
    Object.keys(labels.seriesLabels).length === 0 &&
    Object.keys(labels.columnLabels).length === 0
  ) {
    return null;
  }

  return labels;
}

function buildRenderFormat(spec: VizSpec): VizFollowupRenderFormat {
  return {
    kind: spec.type === "table" ? "table" : "chart",
    viewType: spec.type,
  };
}

/**
 * 永続化済み follow-up context を現在の契約で再利用できる形へ検証する。
 *
 * @remarks
 * chat 履歴は current code より長く残るため、read-side では contract を再検証する。
 * 壊れた payload をそのまま使うと client scan 全体が落ちるため、ここで null を返して
 * 「follow-up facts には使わない」と明示的に扱う。
 */
export function resolveVizFollowupContext(
  value: unknown,
): VizFollowupContext | null {
  const parsed = vizFollowupContextSchema.safeParse(value);
  return parsed.success ? parsed.data : null;
}

/**
 * 継続会話で main LLM が参照しやすい 1 行の fact summary を作る。
 *
 * @remarks
 * `followupContext` をそのまま runtime 側で解釈し始めると責務が散るため、
 * 親 model 向け compact result の圧縮規則は context module 側へ寄せて SSOT にする。
 * ここでの件数制限は prompt shaping ではなく short-result contract の一部として扱う。
 */
export function summarizeVizFollowupContextFacts(
  context: unknown,
): string | null {
  const resolvedContext = resolveVizFollowupContext(context);
  if (!resolvedContext) return null;

  const facts: string[] = [];

  const cardTitle = toCompactScalar(resolvedContext.cardTitle);
  if (cardTitle) facts.push(`title=${cardTitle}`);

  facts.push(
    `view=${resolvedContext.renderFormat.kind}:${resolvedContext.renderFormat.viewType}`,
  );

  const intent = toCompactScalar(resolvedContext.intent);
  if (intent) facts.push(`intent=${intent}`);

  facts.push(`renderedRowCount=${resolvedContext.rowCount}`);

  const seriesLabels = resolvedContext.displayLabels?.seriesLabels ?? {};
  const salientSeriesKeys = selectSalientSeriesKeys(resolvedContext);
  facts.push(`seriesCount=${resolvedContext.seriesKeys.length}`);
  if (salientSeriesKeys.length > 0) {
    facts.push(
      `salientSeries=${salientSeriesKeys
        .map((seriesKey) => formatSeriesLabel(seriesKey, seriesLabels))
        .join(",")}`,
    );
  }
  const omittedSeriesCount = Math.max(
    0,
    resolvedContext.seriesKeys.length - salientSeriesKeys.length,
  );
  if (omittedSeriesCount > 0) {
    facts.push(`omittedSeriesCount=${omittedSeriesCount}`);
  }

  const firstLabel = toCompactScalar(resolvedContext.firstRow?.label);
  const latestLabel = toCompactScalar(resolvedContext.latestRow?.label);
  if (resolvedContext.xKey && firstLabel && latestLabel) {
    facts.push(`range=${firstLabel}..${latestLabel}`);
  }

  const latestRow = formatFollowupRow(
    resolvedContext.latestRow,
    salientSeriesKeys,
    seriesLabels,
  );
  if (latestRow) facts.push(`latest=${latestRow}`);

  const representativeRows = (
    resolvedContext.xKey
      ? resolvedContext.topRows.slice(-3)
      : resolvedContext.topRows.slice(0, 3)
  )
    .map((row) => formatFollowupRow(row, salientSeriesKeys, seriesLabels))
    .filter((value): value is string => Boolean(value))
    .slice(0, 3);
  if (representativeRows.length > 0) {
    facts.push(
      `${resolvedContext.xKey ? "recent" : "top"}=${representativeRows.join(" | ")}`,
    );
  }

  const peaks = resolvedContext.maxBySeries
    .filter((peak) => salientSeriesKeys.includes(peak.seriesKey))
    .sort(
      (left, right) =>
        salientSeriesKeys.indexOf(left.seriesKey) -
        salientSeriesKeys.indexOf(right.seriesKey),
    )
    .flatMap((peak) => {
      const seriesKey = toCompactScalar(
        formatSeriesLabel(peak.seriesKey, seriesLabels),
      );
      const label = toCompactScalar(peak.label);
      const value = toCompactScalar(peak.value);
      if (!seriesKey || !label || !value) return [];
      return [`${seriesKey}@${label}=${value}`];
    })
    .slice(0, MAX_FOLLOWUP_SERIES_FACTS);
  if (peaks.length > 0) {
    facts.push(`peaks=${peaks.join(", ")}`);
  }

  return facts.length > 0 ? `Visualization facts: ${facts.join("; ")}` : null;
}

/**
 * 可視化結果を次ターン会話へ渡すための compact context を構築する。
 *
 * @remarks
 * tree/spec 全体はノイズが大きいため、follow-up で使う情報を
 * summary + evidence digest（top rows / series peak）へ縮約する。
 * これは persisted compact context の契約であり、authoring / narrative prompt
 * 自体の入力を silent に削るための処理ではない。
 */
export function buildVizFollowupContext(args: {
  evidence: VizRenderEvidence;
  spec: VizSpec;
  summary: string | null;
  intent?: string | null;
  beforeCardText?: string | null;
  afterCardText?: string | null;
  cardTitle?: string | null;
}): VizFollowupContext {
  const topRows = buildEvidenceTopRows(args.evidence);
  const intent =
    typeof args.intent === "string" && args.intent.trim().length > 0
      ? args.intent.trim()
      : null;

  return {
    sourceDatasetId: args.evidence.sourceDatasetId,
    renderFormat: buildRenderFormat(args.spec),
    rowCount: args.evidence.rowCount,
    summary: args.summary,
    intent,
    beforeCardText: args.beforeCardText ?? null,
    afterCardText: args.afterCardText ?? null,
    cardTitle: args.cardTitle ?? null,
    categoryKey: args.evidence.categoryKey,
    xKey: args.evidence.xKey,
    seriesKeys: args.evidence.seriesKeys,
    units: args.evidence.units,
    displayLabels: buildDisplayLabelSummary(args.spec),
    topRows,
    maxBySeries: buildEvidenceMaxBySeries(args.evidence),
    firstRow: args.evidence.rows[0] ?? null,
    latestRow: buildLatestEvidenceRow(args.evidence),
  };
}
